import type { FlowDocument } from "./types";

/** The lowest-numbered "e_<n>" not already used — matches the shipped
 * examples' own "e1"/"e2" convention closely enough while guaranteeing
 * uniqueness without scanning their exact numbering scheme. */
export function nextEdgeId(doc: FlowDocument): string {
  const used = new Set(doc.edges.map((edge) => edge.id));
  let n = 1;
  let id = `e_${n}`;
  while (used.has(id)) {
    n += 1;
    id = `e_${n}`;
  }
  return id;
}

/**
 * Appends one new edge with a caller-given port name (FlowCanvas's own
 * onConnect handler defaults new connections to "next", editable
 * immediately afterward in the Inspector).
 *
 * Deliberately does not check for a duplicate source+port or validate
 * the port name against the target node's own type — the real port
 * rules (docs/plans/06's "Semantic validation — implement in Go; mirror
 * diagnostics in web") have never been mirrored to this web side; the
 * existing schema-diagnostics panel is the only feedback a bad edge gets
 * today, same as an invalid node config already gets.
 */
export function addEdge(doc: FlowDocument, id: string, source: string, port: string, target: string): FlowDocument {
  return {
    ...doc,
    edges: [...doc.edges, { id, source, port, target }],
  };
}

/** Replaces one edge's own port name — the pure update Inspector's edge
 * editor hands to onChange, the same shape setNodeConfig already
 * establishes for a node's config. */
export function setEdgePort(doc: FlowDocument, edgeId: string, port: string): FlowDocument {
  return {
    ...doc,
    edges: doc.edges.map((edge) => (edge.id === edgeId ? { ...edge, port } : edge)),
  };
}

/** Removes one edge. Unlike removeNode's start-node guard, no edge is
 * ever structurally required to exist, so this never refuses. */
export function removeEdge(doc: FlowDocument, edgeId: string): FlowDocument {
  return {
    ...doc,
    edges: doc.edges.filter((edge) => edge.id !== edgeId),
  };
}
